import React, { memo, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { LinkRelationshipInlineEditView } from '@jira/issue-field-link-relationship-inline-edit-full/src/ui/index.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections';
import type { LinkRelationshipTypeCell_fieldRef$key } from '@atlassian/jira-relay/src/__generated__/LinkRelationshipTypeCell_fieldRef.graphql';
import type { LinkRelationshipTypeCell_issueRef$key } from '@atlassian/jira-relay/src/__generated__/LinkRelationshipTypeCell_issueRef.graphql';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';

export type Props = {
	fieldRef: LinkRelationshipTypeCell_fieldRef$key;
	issueRef: LinkRelationshipTypeCell_issueRef$key;
	issueStoreId: string; // Relay store ID for the issue row
	itemsConnectionId: string; // Connection ID for the items list
	onIssueUnlink?: (issueKey: string) => void;
};

export const LinkRelationshipTypeCell = memo<Props>(
	({ fieldRef, issueRef, onIssueUnlink, issueStoreId, itemsConnectionId }) => {
		const { overriding } = useInlineEditFieldInjections();

		const issueData = useFragment<LinkRelationshipTypeCell_issueRef$key>(
			graphql`
				fragment LinkRelationshipTypeCell_issueRef on JiraIssue {
					...ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_issueRef
				}
			`,
			issueRef,
		);

		const data = useFragment<LinkRelationshipTypeCell_fieldRef$key>(
			graphql`
				fragment LinkRelationshipTypeCell_fieldRef on JiraIssueLinkRelationshipTypeField
				@argumentDefinitions(
					isInlineEditingEnabled: { type: "Boolean!" }
					isEditableSyncLoaded: { type: "Boolean!" }
				) {
					id
					isEditableInIssueView @include(if: $isEditableSyncLoaded)
					lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
					...ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_fieldRef
				}
			`,
			fieldRef,
		);

		let baseIsEditable: boolean | null | undefined = false;
		if ('isEditableInIssueView' in data) {
			baseIsEditable = data.isEditableInIssueView;
		} else if ('lazyIsEditableInIssueView' in data) {
			baseIsEditable = data.lazyIsEditableInIssueView;
		}
		useLazyLoadEditability(data.id, baseIsEditable);

		const isEditable = useMemo(
			() => overriding.overrideIsEditable(Boolean(baseIsEditable)),
			[baseIsEditable, overriding],
		);

		return (
			<>
				<LinkRelationshipInlineEditView
					fieldRef={data}
					issueRef={issueData}
					isEditable={isEditable ?? false}
					issueStoreId={issueStoreId}
					itemsConnectionId={itemsConnectionId}
					onIssueUnlink={onIssueUnlink}
				/>
			</>
		);
	},
);
